import { useMutation, useQuery } from "@tanstack/react-query";
import type { Logos } from "@optymyse/suite-id";
import { api, ApiError, askApi, type ApiErrorBody } from "./api";
import { centralUrl } from "./hosts";
import type { WorkspaceRole } from "../../shared/schemas/workspaces";

export const ME_KEY = ["me"] as const;

/** A workspace the person may open, as `GET /api/v1/me` lists it: Sustain's shape (src/domains/me/routes.ts). */
export type MeWorkspace = {
  id: string;
  /** Its organisation's name as Impa holds it: what every app calls the workspace, never its address. */
  name: string;
  slug: string;
  role: WorkspaceRole;
  console_url: string;
  public_url: string;
  /** Its organisation's logos, as Impa's decision just now carried them (./workspaces.ts, organisationIcon). */
  logos: Logos;
};

export type Me = {
  kind: "user";
  user: { id: string; email: string; name: string; picture: string | null };
  workspaces: MeWorkspace[];
  /** How many of the person's workspaces Optymyse couldn't be asked about just now, and are left out. */
  unchecked: number;
};

/**
 * What `GET /api/v1/me` says: nobody signed in; the person and the workspaces they may open; signed in
 * with none Optymyse lets them open (403); or signed in while Optymyse can't say (503). Each is an
 * answer, kept as data, not a failure: a query that failed has no data, and every component that
 * starts reading it fetches it again, which here remounted the very layouts that read it, for ever.
 */
export type MeAnswer =
  | { readonly kind: "signed_out" }
  | { readonly kind: "signed_in"; readonly me: Me }
  | { readonly kind: "no_workspace" }
  | { readonly kind: "unavailable" };

async function fetchMe(): Promise<MeAnswer> {
  const res = await api.api.v1.me.$get();
  // The API's refusals are thrown, not returned, so the route's types know only its 200: any status may come.
  const status: number = res.status;
  if (status === 401) return { kind: "signed_out" };
  if (status === 403) return { kind: "no_workspace" };
  if (status === 503) return { kind: "unavailable" };
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Partial<ApiErrorBody>;
    throw new ApiError(status, {
      error: { code: body.error?.code ?? "unknown", message: body.error?.message ?? "Error" },
    });
  }
  return { kind: "signed_in", me: (await res.json()) as Me };
}

export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: fetchMe,
    // Optymyse unavailable is asked about again whenever a page starts reading it; anything else holds a minute.
    staleTime: (query) => (query.state.data?.kind === "unavailable" ? 0 : 60_000),
    refetchOnWindowFocus: true,
    retry: 1,
    retryOnMount: false,
  });
}

/** Whether `useMe` says someone is signed in: with workspaces, with none, or while Optymyse can't say. */
export function signedIn(me: ReturnType<typeof useMe>): boolean {
  return me.data !== undefined && me.data.kind !== "signed_out";
}

/** The signed-in person, when `GET /api/v1/me` named them. */
export function meUser(me: ReturnType<typeof useMe>): Me["user"] | undefined {
  return me.data?.kind === "signed_in" ? me.data.me.user : undefined;
}

/**
 * Signs the person out of the whole suite (Impa docs/PLATFORM_CONVENTIONS.md, rule 9), as Assure's
 * page does. Link ends its session and answers with Optymyse's one-time sign-out address, which ends
 * the Optymyse session and every other app's, then returns to /signed-out. With no address, Optymyse
 * couldn't be asked, and the signed-out page says so. When nothing was ended, a fresh page shows the
 * person they're still signed in.
 */
async function signOut(): Promise<void> {
  let next = centralUrl("/signed-out?impa=unavailable");
  try {
    const res = await askApi("/auth/logout", {
      method: "POST",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });
    if (!res.ok) throw new Error("sign_out_failed");
    const { logout_url: logoutUrl } = (await res.json()) as { logout_url: string | null };
    if (logoutUrl) next = logoutUrl;
  } catch {
    window.location.reload();
    return;
  }
  window.location.replace(next);
}

export function useSignOut() {
  return useMutation({ mutationFn: signOut });
}
